/**
 * Sweet Tooth Custom Search with Fuzzy Matching
 * 
 * Features:
 * - Fuzzy matching (typo tolerance)
 * - Only shows active, published products
 * - Excludes IN STORE collections
 * - Shows results as you type
 * - Keyboard navigation support
 * - Mobile-friendly
 * - Occasion keywords route to basket builder
 */

class SweetToothSearch {
  constructor(options = {}) {
    this.container = options.container || document.getElementById('sweet-tooth-search');
    this.excludedProductIds = new Set([
      // Staff-only custom-price item (never show to customers)
      '9622482387199',
      // IN STORE collection products
      '9098080715007', '9080768037119', '9080768430335', '9080766365951',
      '9094644105471', '9097553215743', '9094630670591', '9009123721471',
      '9094646137087', '9094632276223', '9094627426559', '9080764137727',
      '9080745001215', '9094624510207', '9080765972735',
      '9081546932479', '9094634635519', '9081547751679', '9102954660095',
      '9091266478335', '9094621823231', '9080767054079', '9091396894975',
      '9080767676671', '9080766824703', '9080765710591', '9080765284607',
      '9080764530943', '9080763056383', '9080761647359', '9080737595647',
      '8872460976383',
      // IN STORE Dubai Chocolate
      '9081531695359', '9081531367679',
      // Occasion gift baskets — these are the same basket with a different
      // plaque, so search routes to the Basket Builder instead of showing them.
      '9341580476671', '9341580673279', '9341580574975', '9341580542207'
    ]);
    
    // Occasions & holidays → Basket Builder. Each entry maps (via
    // occasionToBuilderKey) to a builder occasion key when one exists,
    // otherwise it opens the builder generically. Product/category terms
    // (cookies, truffles, etc.) are handled separately in `categories`.
    this.occasions = [
      { keywords: ['birthday', 'bday', 'b-day', 'birthay', 'birtday', 'kids birthday', 'adult birthday', 'milestone birthday', 'employee birthday'], label: 'Birthday', icon: '🎂' },
      { keywords: ['shiva', 'shivah', 'shiva basket', 'condolence', 'condolences', 'sympathy', 'sympathey', 'funeral', 'yahrzeit', 'bereavement', 'mourning', 'sorry for your loss'], label: 'Sympathy & Shiva', icon: '🕊️' },
      { keywords: ['thank you', 'thanks', 'thankyou', 'thank-you', 'appreciation', 'grateful', 'gratitude', 'thank you for hosting', 'hostess gift', 'host gift', 'holiday thank you', 'referral thank you', 'client appreciation', 'customer appreciation', 'employee appreciation', 'caregiver appreciation', 'bosss day', 'boss day', 'administrative professionals', 'admin professionals', 'secretarys day', 'nurses day'], label: 'Thank You', icon: '🙏' },
      { keywords: ['congratulations', 'congrats', 'congratualations', 'congradulations', 'mazel tov', 'mazal tov', 'mazel', 'mazeltov', 'promotion', 'new job', 'grand opening', 'ribbon cutting', 'product launch', 'business anniversary', 'sales incentive'], label: 'Congratulations', icon: '🎉' },
      { keywords: ['get well', 'getwell', 'get-well', 'recovery', 'feel better', 'speedy recovery', 'refuah', 'refua', 'refuah shleimah', 'hospital gift'], label: 'Get Well', icon: '💐' },
      { keywords: ['anniversary', 'aniversary', 'anniversery', 'wedding anniversary', 'jewish anniversary'], label: 'Anniversary', icon: '💕' },
      { keywords: ['wedding', 'bridal', 'bridal shower', 'bride', 'engagement', 'engaged', 'aufruf', 'sheva brachot', 'jewish wedding'], label: 'Wedding', icon: '💒' },
      { keywords: ['baby', 'newborn', 'new baby', 'baby shower', 'gender reveal', 'baby naming', 'simchat bat', 'bris', 'brit milah', 'upsherin', 'upsherenish', 'its a boy', 'its a girl', 'baby girl', 'baby boy', 'bassinet', 'bassinets', 'bassinette', 'basinette', 'basinet'], label: 'New Baby', icon: '👶' },
      { keywords: ['housewarming', 'house warming', 'new home', 'new house', 'moving', 'welcome to the neighborhood', 'chanukat habayit', 'real estate closing', 'closing gift', 'realtor gift', 'mortgage broker gift'], label: 'Housewarming', icon: '🏠' },
      { keywords: ['graduation', 'graduate', 'grad', 'college acceptance', 'accepted', 'college graduation', 'high school graduation', 'off to college', 'back to school', 'finals care package'], label: 'Graduation', icon: '🎓' },
      { keywords: ['teacher', 'teacher appreciation', 'teachers', 'morah', 'rebbe', 'rabbi gift', 'rabbi', 'rebbetzin', 'professor', 'coach gift'], label: 'Teacher Appreciation', icon: '📚' },
      { keywords: ['mothers day', 'mother\'s day', 'mothersday', 'mom', 'mommy', 'madre', 'dog mothers day', 'dog mom'], label: 'Mother\'s Day', icon: '👩' },
      { keywords: ['valentines', 'valentine', 'valentines day', 'valentine\'s day', 'vday', 'v-day'], label: 'Valentine\'s Day', icon: '❤️' },
      { keywords: ['purim', 'mishloach manot', 'shalach manos', 'hamantaschen', 'mishloach'], label: 'Purim', icon: '🎭' },
      { keywords: ['passover', 'pesach', 'seder', 'chol hamoed', 'mimouna'], label: 'Passover', icon: '🍷' },
      { keywords: ['rosh hashanah', 'rosh hashana', 'rosh hashanna', 'jewish new year', 'shanah tovah', 'shana tova'], label: 'Rosh Hashanah', icon: '🍎' },
      { keywords: ['hanukkah', 'chanukah', 'hanukah', 'channukah', 'hannukah'], label: 'Hanukkah', icon: '🕎' },
      { keywords: ['sukkot', 'succot', 'sukkos', 'succos', 'shemini atzeret', 'simchat torah'], label: 'Sukkot', icon: '🌿' },
      { keywords: ['thanksgiving', 'thanks giving', 'turkey day'], label: 'Thanksgiving', icon: '🦃' },
      { keywords: ['christmas', 'xmas', 'x-mas', 'holiday gift', 'holiday gifts', 'secret santa', 'stocking stuffer', 'stocking stuffers', 'office holiday', 'corporate holiday', 'holiday party', 'christmas eve', 'christmas day'], label: 'Christmas & Holidays', icon: '🎄' },
      { keywords: ['easter'], label: 'Easter', icon: '🐰' },
      { keywords: ['corporate', 'corporate gift', 'business gift', 'client gift', 'employee gift', 'vendor gift', 'office gift', 'company gift', 'kosher corporate', 'kosher client', 'hotel welcome', 'employee onboarding', 'employee work anniversary', 'vip gift', 'luxury gift', 'custom logo gift'], label: 'Corporate Gifts', icon: '💼' },
      { keywords: ['just because', 'thinking of you', 'miss you', 'i miss you', 'love you', 'long distance', 'long-distance', 'apology'], label: 'Just Because', icon: '💝' },
      { keywords: ['bar mitzvah', 'bat mitzvah', 'barmitzvah', 'batmitzvah', 'b\'nai mitzvah', 'bnai mitzvah', 'mitzvah'], label: 'Bar/Bat Mitzvah', icon: '✡️' },
      // Catch-all for any other celebration/occasion/holiday → Basket Builder
      { keywords: ['sweet 16', 'sweet sixteen', 'sweet 16th', 'quinceanera', 'quinceañera', 'quince', 'retirement', 'communion', 'first communion', 'confirmation', 'christening', 'baptism', 'bon voyage', 'farewell', 'going away', 'good luck', 'welcome home', 'welcome back', 'reunion', 'family reunion', 'class reunion', 'military reunion', 'military care package', 'care package', 'picnic', 'barbecue', 'cookout', 'game day', 'tailgating', 'dinner party', 'party favor', 'event favor', 'office party', 'team celebration', 'new years', 'new year\'s eve', 'st patricks', 'st patrick', 'halloween', 'memorial day', 'labor day', 'juneteenth', 'independence day', '4th of july', 'july 4th', 'fourth of july', 'grandparents day', 'veterans day', 'presidents day', 'mlk', 'martin luther king', 'columbus day', 'indigenous peoples', 'earth day', 'april fools', 'shabbat', 'shabbos', 'tu bishvat', 'tu b\'shevat', 'lag baomer', 'lag b\'omer', 'tu b\'av', 'tisha b\'av', 'fast of esther', 'tzom gedaliah', 'asara b\'tevet', 'yom hashoah', 'yom hazikaron', 'yom haatzmaut', 'yom ha\'atzmaut', 'yom yerushalayim', 'yom kippur', 'break the fast', 'break-the-fast', 'break fast', 'attorney gift', 'doctor gift', 'nurse gift'], label: 'Special Occasion', icon: '🎉' },
    ];

    // Product categories: route to the full COLLECTION page (no basket builder).
    this.categories = [
      { keywords: ['cookie', 'cookies', 'rugelach', 'rugelah', 'brownie', 'brownies', 'baked', 'bakery', 'black and white'], label: 'Bakery & Cookies', icon: '🍪', collection: 'the-bakery-collection' },
      { keywords: ['truffle', 'truffles'], label: 'Truffles', icon: '🍫', collection: 'truffles' },
      { keywords: ['strawberr', 'strawberries', 'dipped fruit', 'chocolate covered strawberr', 'chocolate dipped strawberr'], label: 'Dipped Strawberries & Fruit', icon: '🍓', collection: 'chocolate-dipped-fruit' },
      { keywords: ['candy apple', 'caramel apple', 'apple', 'apples'], label: 'Candy & Caramel Apples', icon: '🍎', collection: 'candy-apples' },
      { keywords: ['pretzel', 'pretzels', 'oreo', 'oreos', 'pretzel oreo'], label: 'Pretzel & Oreo Trays', icon: '🥨', collection: 'pretzel-oreo-trays' },
      { keywords: ['popcorn', 'mucho gusto', 'mucho'], label: 'Mucho Gusto Munch', icon: '🍿', collection: 'mucho-gusto-munch' },
      { keywords: ['favorite', 'favorites', 'best seller', 'bestseller', 'best-seller', 'most popular', 'popular'], label: 'Customer Favorites', icon: '💜', collection: 'shop-favorites' },
      { keywords: ['trending', 'viral', 'tiktok'], label: 'Trending Now', icon: '🔥', collection: 'viral-edit' },
      { keywords: ['snack', 'snacks'], label: 'Snacks & Sweets', icon: '🍬', collection: 'snacks-and-sweets' },
      { keywords: ['dipped', 'chocolate dipped', 'chocolate-dipped'], label: 'Chocolate-Dipped Treats', icon: '🍫', collection: 'chocolate-dipped-treats' },
    ];
    
    // Generic store nouns that must never trigger an occasion suggestion.
    // (A full phrase like "new baby" or "thank you" still matches — this only
    // blocks the bare generic word typed alone.)
    this.genericTerms = new Set([
      'basket','baskets','gift','gifts','gifting','chocolate','chocolates','choc',
      'box','boxes','tray','trays','treat','treats','candy','candies',
      'sweet','sweets','snack','snacks','platter','platters',
      'day','days','you','your','yours','new','order','orders',
      'custom','customized','kosher','parve','pareve','logo','care','package','packages',
      'present','presents','delivery','shipping','same','store','shop','buy','online',
      'best','popular','food','dessert','desserts','assorted','assortment',
      'small','medium','large','big','cheap','price','prices',
      // fragment/size/descriptor words that leaked odd occasions (see scan)
      'mini','dog','good','first','high','real','back','off','king','secret','grand','round','oval'
    ]);

    this.products = [];
    this.filteredProducts = [];
    this.matchedOccasions = [];
    this.matchedCategories = [];
    this.selectedIndex = -1;
    this.isOpen = false;
    this.debounceTimer = null;
    this.minChars = 2;
    this.maxResults = 6; // Reduced to make room for occasions
    this.maxOccasions = 3;
    
    this.init();
  }

  async init() {
    this.render();
    this.attachEventListeners();
    await this.loadProducts();
  }

  render() {
    this.container.innerHTML = `
      <div class="sts-search-wrapper">
        <div class="sts-search-input-wrapper">
          <svg class="sts-search-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.3-4.3"></path>
          </svg>
          <input 
            type="text" 
            class="sts-search-input" 
            placeholder="Search chocolates, gifts, occasions..."
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            aria-label="Search products"
            aria-expanded="false"
            aria-controls="sts-search-results"
            aria-autocomplete="list"
            role="combobox"
          >
          <button class="sts-search-clear" type="button" aria-label="Clear search" style="display: none;">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>
        <div class="sts-search-results" id="sts-search-results" role="listbox" style="display: none;">
          <div class="sts-search-loading">Searching...</div>
        </div>
      </div>
    `;

    this.input = this.container.querySelector('.sts-search-input');
    this.results = this.container.querySelector('.sts-search-results');
    this.clearBtn = this.container.querySelector('.sts-search-clear');
  }

  attachEventListeners() {
    // Input events
    this.input.addEventListener('input', (e) => this.handleInput(e));
    this.input.addEventListener('focus', () => this.handleFocus());
    this.input.addEventListener('keydown', (e) => this.handleKeydown(e));
    
    // Clear button
    this.clearBtn.addEventListener('click', () => this.clearSearch());
    
    // Click outside to close
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target)) {
        this.closeResults();
      }
    });
  }

  async loadProducts() {
    try {
      // Use Shopify's Storefront API via the standard products.json endpoint
      const response = await fetch('/products.json?limit=250');
      const data = await response.json();
      
      // Filter out excluded products and build search index
      this.products = data.products
        .filter(p => !this.excludedProductIds.has(String(p.id)))
        // Past-holiday products stay `active` so Google keeps their page, but they
        // must not show in the on-site search until their season comes round again.
        .filter(p => !(p.tags || []).includes('off-season'))
        // nosearch = live product kept out of general search on purpose
        .filter(p => !(p.tags || []).includes('nosearch'))
        // Hide basket add-ons / extras (upsells) from search results
        .filter(p => !/add[\s-]?on/i.test(p.product_type || ''))
        .map(p => ({
          id: p.id,
          title: p.title,
          handle: p.handle,
          url: `/products/${p.handle}`,
          price: this.formatPrice(p.variants[0]?.price),
          comparePrice: p.variants[0]?.compare_at_price ? this.formatPrice(p.variants[0].compare_at_price) : null,
          image: p.images[0]?.src ? this.resizeImage(p.images[0].src, 100) : null,
          vendor: p.vendor,
          type: p.product_type,
          tags: p.tags || [],
          // Create searchable text — title + type only.
          // Tags/vendor are intentionally excluded: products are broadly
          // tagged by occasion (e.g. "birthday"), which polluted results
          // with irrelevant items. Occasion intent is handled separately
          // and routed to the Basket Builder.
          searchText: [
            p.title,
            p.product_type
          ].join(' ').toLowerCase()
        }));

      // De-duplicate by title — some products are catalog duplicates
      // (e.g. two "Caramel Chocolate Apple" entries) and shouldn't show twice.
      const seenTitles = new Set();
      this.products = this.products.filter(p => {
        const key = p.title.trim().toLowerCase();
        if (seenTitles.has(key)) return false;
        seenTitles.add(key);
        return true;
      });

      console.log(`Sweet Tooth Search: Loaded ${this.products.length} products`);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  }

  formatPrice(price) {
    if (!price) return null;
    const num = parseFloat(price);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  }

  resizeImage(src, size) {
    if (!src) return null;
    // Shopify image URL transformation
    return src.replace(/\.([^.]+)$/, `_${size}x.$1`);
  }

  handleInput(e) {
    const query = e.target.value.trim();
    
    // Show/hide clear button
    this.clearBtn.style.display = query.length > 0 ? 'flex' : 'none';
    
    // Debounce search
    clearTimeout(this.debounceTimer);
    
    if (query.length < this.minChars) {
      this.closeResults();
      return;
    }
    
    this.debounceTimer = setTimeout(() => {
      this.search(query);
    }, 150);
  }

  handleFocus() {
    const query = this.input.value.trim();
    if (query.length >= this.minChars && (this.filteredProducts.length > 0 || this.matchedOccasions.length > 0)) {
      this.openResults();
    }
  }

  handleKeydown(e) {
    if (!this.isOpen) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.navigateResults(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.navigateResults(-1);
        break;
      case 'Enter':
        e.preventDefault();
        this.selectResult();
        break;
      case 'Escape':
        this.closeResults();
        this.input.blur();
        break;
    }
  }

  navigateResults(direction) {
    const items = this.results.querySelectorAll('.sts-search-item, .sts-occasion-item');
    if (items.length === 0) return;
    
    // Remove current selection
    items.forEach(item => item.classList.remove('sts-selected'));
    
    // Calculate new index
    this.selectedIndex += direction;
    if (this.selectedIndex < 0) this.selectedIndex = items.length - 1;
    if (this.selectedIndex >= items.length) this.selectedIndex = 0;
    
    // Apply new selection
    const selected = items[this.selectedIndex];
    selected.classList.add('sts-selected');
    selected.scrollIntoView({ block: 'nearest' });
  }

  selectResult() {
    const items = this.results.querySelectorAll('.sts-search-item, .sts-occasion-item');
    if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
      const url = items[this.selectedIndex].dataset.url;
      if (url) window.location.href = url;
    }
  }

  searchCategories(query) {
    const normalizedQuery = query.toLowerCase().trim();
    const matched = [];

    for (const category of this.categories) {
      for (const keyword of category.keywords) {
        if (keyword.includes(normalizedQuery) || normalizedQuery.includes(keyword)) {
          matched.push(category);
          break;
        }
        if (normalizedQuery.length >= 4 && this.levenshteinDistance(normalizedQuery, keyword) <= 1) {
          matched.push(category);
          break;
        }
      }
    }

    return [...new Map(matched.map(m => [m.label, m])).values()].slice(0, 2);
  }

  occasionScore(query, occasion) {
    // Best relevance score across an occasion's keywords. Higher = better match.
    // Exact keyword wins over prefix wins over substring wins over fuzzy, so the
    // term the user actually typed always ranks first (e.g. "anniversary" must
    // beat "business anniversary" on Congratulations).
    let best = 0;
    for (const kw of occasion.keywords) {
      let s = 0;
      if (kw === query) s = 1000;                                         // exact keyword
      else if (kw.startsWith(query) || query.startsWith(kw)) s = 500;     // prefix either way
      else if (query.length >= 4 && kw.includes(query)) s = 300;          // query is a chunk of keyword (len-guarded)
      else if (query.length >= 4 && kw.length >= 4 && query.includes(kw)) s = 250; // keyword is a chunk of query
      else if (query.length >= 5 && this.levenshteinDistance(query, kw) === 1) s = 150; // tight typo tolerance
      if (s > best) best = s;
    }
    return best;
  }

  searchOccasions(query) {
    const q = query.toLowerCase().trim();
    if (q.length < 2) return [];
    // Generic store words (basket, gift, chocolate, day, you, new…) are NOT
    // occasion triggers — never surface an occasion for them (e.g. "basket"
    // must never show Shiva just because "shiva basket" is a keyword).
    if (this.genericTerms.has(q)) return [];
    const scored = [];
    this.occasions.forEach((occasion, idx) => {
      const score = this.occasionScore(q, occasion);
      if (score > 0) scored.push({ occasion, score, idx });
    });
    if (scored.length === 0) return [];
    // Rank by score; tie-break by original order for stability.
    scored.sort((a, b) => b.score - a.score || a.idx - b.idx);
    // Tiered cutoff so we never "steer" the user elsewhere:
    //  - exact keyword typed (1000) → show ONLY exact-match occasions
    //  - prefix match (500)        → show only prefix-or-better
    //  - otherwise (typo/fuzzy)    → show whatever matched
    const top = scored[0].score;
    const threshold = top >= 1000 ? 1000 : (top >= 500 ? 500 : 1);
    return scored.filter(s => s.score >= threshold).slice(0, this.maxOccasions).map(s => s.occasion);
  }

  search(query) {
    if (this.products.length === 0) {
      this.showLoading();
      return;
    }
    
    const normalizedQuery = query.toLowerCase();

    // Search for matching occasions and product categories
    this.matchedOccasions = this.searchOccasions(query);
    this.matchedCategories = this.searchCategories(query);

    // Score each product
    const scored = this.products.map(product => {
      const score = this.calculateScore(normalizedQuery, product);
      return { product, score };
    });
    
    // Filter and sort by score
    const ranked = scored
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(s => s.product);

    this.filteredProducts = this.filterCrossOccasionNoise(ranked).slice(0, this.maxResults);

    this.renderResults();
  }

  filterCrossOccasionNoise(products) {
    // On an occasion search, a product that clearly belongs to a DIFFERENT
    // occasion is noise — "thank you" must never surface Thanksgiving turkeys,
    // and "mother's day" must never surface birthday numbers.
    if (this.matchedOccasions.length === 0) return products;

    const matchedLabels = new Set(this.matchedOccasions.map(o => o.label));
    const ownKeywords = [];
    const otherKeywords = [];
    for (const occ of this.occasions) {
      for (const kw of occ.keywords) {
        if (matchedLabels.has(occ.label)) ownKeywords.push(kw);
        else if (kw.length >= 5) otherKeywords.push(kw);
      }
    }

    // Whole-phrase match so "thanks" does not match inside "thanksgiving".
    const hasPhrase = (text, phrase) =>
      new RegExp('(^|[^a-z])' + phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '([^a-z]|$)', 'i').test(text);

    return products.filter((product) => {
      const title = (product.title || '').toLowerCase();
      if (ownKeywords.some((kw) => hasPhrase(title, kw))) return true;
      return !otherKeywords.some((kw) => hasPhrase(title, kw));
    });
  }

  calculateScore(query, product) {
    let score = 0;
    const title = product.title.toLowerCase();
    const searchText = product.searchText;
    
    // Exact match in title (highest priority)
    if (title.includes(query)) {
      score += 100;
      // Bonus for match at start
      if (title.startsWith(query)) score += 50;
    }
    
    // Check each word in the query
    const queryWords = query.split(/\s+/);
    for (const word of queryWords) {
      if (word.length < 2) continue;
      
      // Exact word match in searchText
      if (searchText.includes(word)) {
        score += 30;
      } else {
        // Fuzzy match
        const fuzzyScore = this.fuzzyMatch(word, searchText);
        score += fuzzyScore;
      }
    }
    
    return score;
  }

  fuzzyMatch(query, text) {
    // Simple fuzzy matching with Levenshtein-inspired approach
    // Check if any word in text is similar to query
    const words = text.split(/\s+/);
    let bestScore = 0;
    
    for (const word of words) {
      if (word.length < 2) continue;
      
      // Check for typo tolerance (1-2 character differences)
      const distance = this.levenshteinDistance(query, word.substring(0, query.length + 2));
      
      if (distance === 0) {
        bestScore = Math.max(bestScore, 25);
      } else if (distance === 1 && query.length >= 3) {
        bestScore = Math.max(bestScore, 15);
      } else if (distance === 2 && query.length >= 5) {
        bestScore = Math.max(bestScore, 8);
      }
      
      // Prefix match
      if (word.startsWith(query.substring(0, Math.min(3, query.length)))) {
        bestScore = Math.max(bestScore, 10);
      }
    }
    
    return bestScore;
  }

  levenshteinDistance(s1, s2) {
    // Optimized for short strings
    if (s1 === s2) return 0;
    if (s1.length === 0) return s2.length;
    if (s2.length === 0) return s1.length;
    
    const matrix = [];
    
    for (let i = 0; i <= s1.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= s2.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= s1.length; i++) {
      for (let j = 1; j <= s2.length; j++) {
        const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
      }
    }
    
    return matrix[s1.length][s2.length];
  }

  occasionToCollection(label) {
    const map = {
      'Birthday': 'birthday',
      'Sympathy & Shiva': 'sympathy-shiva',
      'Thank You': 'thank-you',
      'Congratulations': 'congratulations',
      'Get Well': 'get-well',
      'Anniversary': 'anniversary',
      'New Baby': 'baby',
      'Graduation': 'graduation',
      'Teacher Appreciation': 'teacher-appreciation',
      "Mother's Day": 'mothers-day-2026',
      "Father's Day": 'fathers-day',
      'Corporate Gifts': 'custom-and-corporate'
    };
    return map[label] || '';
  }

  occasionToShopCollection(label) {
    // Occasion → real COLLECTION page (a product grid you can buy from).
    // Never point at /pages/* — those are article-style pages and shoppers
    // coming out of search want products, not paragraphs.
    // Only listed here when the collection holds 3+ live products; thinner
    // occasions fall through to the Basket Builder link alone.
    const map = {
      'Birthday': 'birthday',
      'Sympathy & Shiva': 'sympathy-shiva',
      'Thank You': 'thank-you',
      'Congratulations': 'congratulations',
      'Get Well': 'get-well',
      'Anniversary': 'anniversary',
      'New Baby': 'baby',
      'Teacher Appreciation': 'teacher-appreciation',
      'Rosh Hashanah': 'rosh-hashanah',
      'Hanukkah': 'hanukkah',
      'Thanksgiving': 'thanksgiving',
      'Corporate Gifts': 'custom-and-corporate'
    };
    return map[label] || '';
  }

  occasionToBuilderKey(label) {
    // Map a search occasion label to the Basket Builder's ?occasion= key.
    // Keys must match the builder's occasionMap. '' = no card; lands on step 1.
    const map = {
      'Birthday': 'birthday',
      'Sympathy & Shiva': 'shiva',
      'Thank You': 'thank-you',
      'Congratulations': 'congratulations',
      'Get Well': 'get-well',
      'Anniversary': 'anniversary',
      'Wedding': '',
      'New Baby': 'baby',
      'Housewarming': 'housewarming',
      'Graduation': 'graduation',
      'Teacher Appreciation': 'teacher',
      "Mother's Day": '',
      "Father's Day": 'fathers-day',
      "Valentine's Day": 'valentines',
      'Purim': '',
      'Passover': 'passover',
      'Rosh Hashanah': 'rosh-hashanah',
      'Hanukkah': 'hanukkah',
      'Sukkot': 'sukkot',
      'Thanksgiving': 'thanksgiving',
      'Christmas & Holidays': 'christmas',
      'Easter': '',
      'Corporate Gifts': 'corporate',
      'Just Because': 'just-because',
      'Bar/Bat Mitzvah': ''
    };
    return map[label] || '';
  }

  renderResults() {
    this.selectedIndex = -1;
    let html = '';
    
    const arrow = `<span class="sts-occasion-arrow"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></span>`;

    // Render matched product categories first (full collection pages)
    if (this.matchedCategories.length > 0) {
      html += `<div class="sts-occasions-section">
        <div class="sts-section-header">Shop the collection</div>`;
      html += this.matchedCategories.map((cat) => `
        <a href="/collections/${cat.collection}" class="sts-occasion-item" data-url="/collections/${cat.collection}" role="option">
          <span class="sts-occasion-icon">${cat.icon}</span>
          <span class="sts-occasion-label">Browse all ${this.escapeHtml(cat.label)}</span>
          ${arrow}
        </a>
      `).join('');
      html += `</div>`;
    }

    // Render matched occasions: each occasion is the same basket with a
    // different plaque, so send the customer into the Basket Builder with
    // that occasion pre-selected (the builder reads ?occasion=<key>).
    if (this.matchedOccasions.length > 0) {
      html += `<div class="sts-occasions-section">
        <div class="sts-section-header">Start your gift basket</div>`;

      html += this.matchedOccasions.map((occasion) => {
        const collection = this.occasionToShopCollection(occasion.label);
        const key = this.occasionToBuilderKey(occasion.label);
        const builderUrl = key ? `/pages/build-a-basket?occasion=${key}` : '/pages/build-a-basket';
        const article = /^[aeiou]/i.test(occasion.label) ? 'an' : 'a';
        // 1) Basket Builder — the PRIMARY path, shown for every occasion (the root of the business).
        let occHtml = `
        <a href="${builderUrl}" class="sts-occasion-item" data-url="${builderUrl}" role="option">
          <span class="sts-occasion-icon">${occasion.icon}</span>
          <span class="sts-occasion-label">Build ${article} ${this.escapeHtml(occasion.label)} basket</span>
          ${arrow}
        </a>`;
        // 2) Ready-made products — the occasion COLLECTION, shown IN ADDITION
        //    to the builder when that collection has products to sell.
        if (collection) {
          occHtml += `
        <a href="/collections/${collection}" class="sts-occasion-item" data-url="/collections/${collection}" role="option">
          <span class="sts-occasion-icon">${occasion.icon}</span>
          <span class="sts-occasion-label">Shop ${this.escapeHtml(occasion.label)} gifts</span>
          ${arrow}
        </a>`;
        }
        return occHtml;
      }).join('');

      html += `</div>`;
    }
    
    // Render products. Shoppers are here to buy, so any matching product is
    // shown — including on occasion searches, under the builder/collection links.
    if (this.filteredProducts.length > 0) {
      if (this.matchedCategories.length > 0 || this.matchedOccasions.length > 0) {
        html += `<div class="sts-section-header">Products</div>`;
      }
      
      html += this.filteredProducts.map((product, index) => `
        <a href="${product.url}" class="sts-search-item" data-url="${product.url}" data-index="${index}" role="option">
          <div class="sts-search-item-image">
            ${product.image 
              ? `<img src="${product.image}" alt="${this.escapeHtml(product.title)}" loading="lazy">` 
              : `<div class="sts-search-item-placeholder">🍫</div>`
            }
          </div>
          <div class="sts-search-item-info">
            <div class="sts-search-item-title">${this.escapeHtml(product.title)}</div>
            <div class="sts-search-item-price">
              ${product.comparePrice 
                ? `<span class="sts-compare-price">${product.comparePrice}</span>` 
                : ''
              }
              ${product.price || ''}
            </div>
          </div>
        </a>
      `).join('');
    }
    
    // No results
    if (this.matchedOccasions.length === 0 && this.matchedCategories.length === 0 && this.filteredProducts.length === 0) {
      html = `
        <div class="sts-occasions-section">
          <div class="sts-section-header">Start your gift basket</div>
          <a href="/pages/build-a-basket" class="sts-occasion-item" data-url="/pages/build-a-basket" role="option">
            <span class="sts-occasion-icon">✨</span>
            <span class="sts-occasion-label">Build a custom gift basket</span>
            ${arrow}
          </a>
        </div>
      `;
    }
    
    this.results.innerHTML = html;
    this.openResults();
    this.input.setAttribute('aria-expanded', 'true');
  }

  showLoading() {
    this.results.innerHTML = `<div class="sts-search-loading">Loading products...</div>`;
    this.openResults();
  }

  openResults() {
    this.results.style.display = 'block';
    this.isOpen = true;
  }

  closeResults() {
    this.results.style.display = 'none';
    this.isOpen = false;
    this.selectedIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
  }

  clearSearch() {
    this.input.value = '';
    this.clearBtn.style.display = 'none';
    this.closeResults();
    this.input.focus();
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Auto-initialize all Sweet Tooth search containers
document.addEventListener('DOMContentLoaded', () => {
  // Main homepage search
  const container = document.getElementById('sweet-tooth-search');
  if (container) {
    window.sweetToothSearch = new SweetToothSearch({ container });
  }

  // Header search (compact)
  const headerContainer = document.getElementById('sweet-tooth-search-header');
  if (headerContainer) {
    window.sweetToothSearchHeader = new SweetToothSearch({ container: headerContainer });
  }
});

// Export for manual initialization
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SweetToothSearch;
}




